import React from 'react';
import {
  CheckCircle,
  XCircle,
  FileText,
  Clock,
  CreditCard,
  AlertTriangle,
  TrendingUp,
  Bell,
} from 'lucide-react';
import type { Notification, NotificationType } from '@/types/notification';

const iconMap: Record<NotificationType, React.ElementType> = {
  receipt_processed: CheckCircle,
  receipt_failed: XCircle,
  report_ready: FileText,
  trial_expiring: Clock,
  payment_success: CreditCard,
  payment_failed: AlertTriangle,
  budget_alert: TrendingUp,
  system: Bell,
};

const colorMap: Record<NotificationType, string> = {
  receipt_processed: 'text-green-500 bg-green-100 dark:bg-green-900/30',
  receipt_failed: 'text-red-500 bg-red-100 dark:bg-red-900/30',
  report_ready: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
  trial_expiring: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900/30',
  payment_success: 'text-green-500 bg-green-100 dark:bg-green-900/30',
  payment_failed: 'text-red-500 bg-red-100 dark:bg-red-900/30',
  budget_alert: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30',
  system: 'text-gray-500 bg-gray-100 dark:bg-gray-900/30',
};

interface NotificationItemProps {
  notification: Notification;
  onClick?: () => void;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Agora mesmo';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}min atras`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h atras`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d atras`;
  }

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const Icon = iconMap[notification.type] || Bell;
  const colorClass = colorMap[notification.type] || colorMap.system;

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-3 p-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-secondary-700 ${
        !notification.read ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''
      }`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium truncate ${
            !notification.read
              ? 'text-gray-900 dark:text-white'
              : 'text-gray-700 dark:text-gray-300'
          }`}>
            {notification.title}
          </p>
          {!notification.read && (
            <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0 mt-1.5" />
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          {formatTimeAgo(notification.createdAt)}
        </p>
      </div>
    </button>
  );
};

export default NotificationItem;
